import React, {useState, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {firestore, functions} from '../Firebase';
import {uploadCommercialLicenseToStorage} from '../../services/storage-service';

const schema = yup.object().shape({
    companyName: yup.string().required('Company name is required').min(3),
    address: yup.string().required('Address 1 is required').min(5),
    street: yup.string().nullable(),
    phoneNumber: yup
        .string()
        .required('Phone number is required')
        .matches(/^\d{7,14}$/, 'Phone number must be between 7 and 14 digits'),

    contactPersonName: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .test('optional-validation', 'Contact person name must only contain alphabetic characters',
            function (value) {
                return !value || /^[A-Za-z\s]+$/.test(value);
            })
        .test('optional-length', 'Contact person name must be at least 3 characters',
            function (value) {
                return !value || value.length >= 3;
            }),

    contactPersonJobTitle: yup
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .test('optional-validation', 'Job title must only contain alphabetic characters',
            function (value) {
                return !value || /^[A-Za-z\s]+$/.test(value);
            })
        .test('optional-length', 'Job title must be at least 3 characters',
            function (value) {
                return !value || value.length >= 3;
            }),

    contactPersonPhoneNumber: yup
        .string()
        .required('Contact person phone number is required')
        .matches(/^\d{7,14}$/, 'Phone number must be between 7 and 14 digits'),

    contactPersonEmail: yup
        .string()
        .required('Contact person email is required')
        .email('Invalid email format'),

    commercialLicense: yup
        .mixed()
        .nullable()
        .test('fileSize', 'File size should be less than 10MB', (value) => {
            return !value || !value[0] || value[0].size <= 10 * 1024 * 1024; // 10MB in bytes
        })
        .test('fileType', 'Unsupported File Format', (value) => {
            if (!value || !value[0]) return true;
            const allowedTypes = [
                'image/jpg',
                'image/jpeg',
                'image/png',
                'image/gif',
                'image/tiff',
                'application/pdf'
            ];
            return allowedTypes.includes(value[0].type);
        }),
});

export default function Register() {
    const [linkData, setLinkData] = useState();
    const [isFormSubmit, setFormSubmit] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const {
        register,
        handleSubmit,
        control,
        setError,
        reset,
        formState: {errors, isDirty, isSubmitting},
        watch,
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            companyName: '',
            street: '',
            address: '',
            phoneNumber: '',
            contactPersonName: '',
            contactPersonJobTitle: '',
            contactPersonPhoneNumber: '',
            contactPersonEmail: '',
            commercialLicense: null,
        },
    });
    const watchCompanyName = watch('companyName');
    const watchAddress = watch('address');
    const watchPhoneNumber = watch('phoneNumber');
    const watchContactPersonEmail = watch('contactPersonEmail');
    const watchContactPersonPhoneNumber = watch('contactPersonPhoneNumber');
    const isRequiredFieldsValid = () => {
        return (
            watchCompanyName &&
            watchCompanyName.length >= 3 &&
            watchAddress &&
            watchAddress.length >= 5 &&
            watchPhoneNumber &&
            /^\d{7,14}$/.test(watchPhoneNumber) &&
            watchContactPersonEmail &&
            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(watchContactPersonEmail) &&
            watchContactPersonPhoneNumber &&
            /^\d{7,14}$/.test(watchContactPersonPhoneNumber) &&
            !Object.keys(errors).some(key => ['companyName', 'address', 'phoneNumber', 'contactPersonEmail', 'contactPersonPhoneNumber'].includes(key))
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const docSnap = await firestore
                    .collection('companies')
                    .doc('links')
                    .get();
                if (docSnap.exists) setLinkData(docSnap.data());
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();
    }, []);

    // Clear success message after 10 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const onSubmit = async (data) => {
        setFormSubmit(true);
        setGeneralError(''); // Clear any previous error messages
        setSuccessMessage(''); // Clear any previous success messages
        try {
            let commercialLicenseUrl = null;

            // Upload commercial license to Firebase Storage if provided
            if (data.commercialLicense && data.commercialLicense[0]) {
                setIsUploading(true);
                try {
                    const file = data.commercialLicense[0];
                    commercialLicenseUrl = await uploadCommercialLicenseToStorage(file, data.companyName);
                } catch (uploadError) {
                    console.error('Error uploading commercial license:', uploadError);
                    setFormSubmit(false);
                    setIsUploading(false);
                    setGeneralError(uploadError.message || 'Failed to upload commercial license. Please try again.');
                    return;
                } finally {
                    setIsUploading(false);
                }
            }

            // Prepare company data with download URL instead of base64
            const companyData = {
                companyName: data.companyName,
                street: data.street || null,
                address: data.address,
                phoneNumber: data.phoneNumber,
                contactPersonName: data.contactPersonName || null,
                contactPersonJobTitle: data.contactPersonJobTitle || null,
                contactPersonPhoneNumber: data.contactPersonPhoneNumber,
                contactPersonEmail: data.contactPersonEmail,
                commercialLicenseUrl: commercialLicenseUrl,
                lastUpdated: new Date().toISOString(),
            };
            
            const registerCompany = functions.httpsCallable('registerCompany');
            await registerCompany(companyData);
            reset();
            setFormSubmit(false);
            setSuccessMessage('Thank you for your registration. It is under review.');
        } catch (error) {
            setFormSubmit(false);
            setIsUploading(false);
            console.error('Error submitting form: ', error);

            const errorMessage = error.message || 'Error submitting form. Please try again.';
            if (error.code === 'functions/invalid-argument' && error.details) {
                try {
                    const errorDetails = typeof error.details === 'string'
                        ? JSON.parse(error.details)
                        : error.details;
                    if (errorDetails.fieldErrors) {
                        Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
                            setError(field, {
                                type: 'manual',
                                message: message
                            });
                        });
                        if (errorDetails.message) {
                            setGeneralError(errorDetails.message);
                        } else {
                            setGeneralError('Please correct the errors in the form.');
                        }
                    } else {
                        setGeneralError(`Validation error: ${error.details}`);
                    }
                } catch (e) {
                    setGeneralError(`Validation error: ${error.details}`);
                }
            } else {
                setGeneralError(errorMessage);
            }
        }
    };

    const fileName = watch('commercialLicense')?.[0]?.name;

    return (
        <div className="home register-page">
            <Helmet>
                <title>Trippie Air - Register Company</title>
                <meta name="description" content="Trippie Air"/>
            </Helmet>
            <div className="home-hero bg-gradient register-page-hero">
                <div className="container-middle-content">
                    <div className="about-header-text">
                        <h1 className="medium-heading">
                            Logistic Company Partner Registration
                        </h1>
                        <p className="medium-para">
                            Please fill out the form below with your company details
                        </p>
                    </div>
                </div>
            </div>
            <div className="container mt-0 mb-5 register-page-container">
                <div className="row">
                    <div className="col-12">
                        <form onSubmit={handleSubmit(onSubmit)} className="register-card">
                            <div className="register-card-body">
                                {generalError && (
                                    <div className="alert alert-danger mb-3">
                                        {generalError}
                                    </div>
                                )}
                                {successMessage && (
                                    <div className="alert alert-success mb-3">
                                        {successMessage}
                                    </div>
                                )}

                                <div className="register-form-section">
                                <h2 className="register-section-title">Company details</h2>
                                <div className="row">
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="companyName">Company Name <span className="required-star">*</span></label>
                                        <input
                                            type="text"
                                            required
                                            className="form-control"
                                            id="companyName"
                                            placeholder="Enter company name"
                                            {...register('companyName')}
                                        />
                                        <p className="text-danger">{errors.companyName?.message}</p>
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="phoneNumber">Phone Number <span className="required-star">*</span></label>
                                        <input
                                            type="tel"
                                            required
                                            className="form-control"
                                            id="phoneNumber"
                                            placeholder="Enter phone number"
                                            {...register('phoneNumber')}
                                        />
                                        <p className="text-danger">{errors.phoneNumber?.message}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="register-form-section">
                                <h2 className="register-section-title">Contact person</h2>
                                <div className="row">
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="contactPersonName">Contact Person Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="contactPersonName"
                                            placeholder="Enter contact person name"
                                            {...register('contactPersonName')}
                                        />
                                        <p className="text-danger">{errors.contactPersonName?.message}</p>
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="contactPersonJobTitle">Contact Person Job Title</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="contactPersonJobTitle"
                                            placeholder="Enter job title"
                                            {...register('contactPersonJobTitle')}
                                        />
                                        <p className="text-danger">{errors.contactPersonJobTitle?.message}</p>
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="contactPersonPhoneNumber">Contact Person Phone <span className="required-star">*</span></label>
                                        <input
                                            type="tel"
                                            required
                                            className="form-control"
                                            id="contactPersonPhoneNumber"
                                            placeholder="Enter contact person phone"
                                            {...register('contactPersonPhoneNumber')}
                                        />
                                        <p className="text-danger">{errors.contactPersonPhoneNumber?.message}</p>
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="contactPersonEmail">Contact Person Email <span className="required-star">*</span></label>
                                        <input
                                            type="email"
                                            required
                                            className="form-control"
                                            id="contactPersonEmail"
                                            placeholder="Enter contact person email"
                                            {...register('contactPersonEmail')}
                                        />
                                        <p className="text-danger">{errors.contactPersonEmail?.message}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="register-form-section">
                                <h2 className="register-section-title">Address</h2>
                                <div className="row register-address-row">
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="address">Address 1 <span className="required-star">*</span></label>
                                        <textarea
                                            className="form-control"
                                            id="address"
                                            rows={4}
                                            required
                                            placeholder="Enter primary address"
                                            {...register('address')}
                                        />
                                        <p className="text-danger">{errors.address?.message}</p>
                                    </div>
                                    <div className="col-md-6 form-group">
                                        <label htmlFor="street">Address 2</label>
                                        <textarea
                                            className="form-control"
                                            id="street"
                                            rows={4}
                                            placeholder="Enter additional address information"
                                            {...register('street')}
                                        />
                                        <p className="text-danger">{errors.street?.message}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="register-form-section">
                                <h2 className="register-section-title">Documents</h2>
                                <div className="form-group">
                                    <label>Upload Commercial License</label>
                                    <p className="register-file-name mb-2" style={{ fontSize: '0.8125rem', color: '#666' }}>
                                        .jpeg, .pdf, .png, .jpg, .tiff, .gif (max 10MB)
                                    </p>
                                    <div className="register-file-zone">
                                        <div className="register-file-input-wrap">
                                            <Controller
                                                name="commercialLicense"
                                                control={control}
                                                render={({ field }) => (
                                                    <>
                                                        <input
                                                            type="file"
                                                            id="commercialLicense"
                                                            accept=".jpg,.jpeg,.tiff,.gif,.pdf,.png"
                                                            onChange={(e) => field.onChange(e.target.files)}
                                                            className="register-file-input"
                                                        />
                                                        <label htmlFor="commercialLicense" className="register-file-label">
                                                            Choose file
                                                        </label>
                                                    </>
                                                )}
                                            />
                                        </div>
                                        {fileName && <span className="register-file-name">{fileName}</span>}
                                    </div>
                                    <p className="text-danger">{errors.commercialLicense?.message}</p>
                                </div>
                            </div>

                                <div className="register-submit-wrap">
                                    <button
                                        type="submit"
                                        disabled={!isRequiredFieldsValid() || isSubmitting || isFormSubmit || isUploading}
                                        className="register-submit-btn"
                                    >
                                        {isUploading ? 'Uploading license...' : isFormSubmit ? 'Submitting...' : 'Submit'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
